import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import InterestedInCardForm from './InterestedInCardForm';
import '../AllCss/InterestedInStep2.css';

export default function InterestedInStep2({
  pendingWalkIn,
  user,
  feeDisplay = '$150',
  onComplete,
  returnFromSquare = false,
  walkInIdFromUrl = '',
}) {
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(returnFromSquare);
  const [config, setConfig] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);

  const isPurchase = pendingWalkIn?.type === 'purchase';
  const displayFee = config?.sourcingFeeDisplay || feeDisplay;

  const vehicleSummary = (() => {
    if (!pendingWalkIn) return '';
    if (pendingWalkIn.type === 'sell') {
      const parts = [pendingWalkIn.sellYear, pendingWalkIn.sellMake, pendingWalkIn.sellModel].filter(Boolean);
      return parts.length ? parts.join(' ') : 'Sell or trade';
    }
    const trim =
      pendingWalkIn.interestTrim && pendingWalkIn.interestTrim !== 'Any'
        ? pendingWalkIn.interestTrim
        : null;
    const parts = [pendingWalkIn.interestMake, pendingWalkIn.interestModel, trim].filter(Boolean);
    return parts.length ? parts.join(' ') : 'Buy a vehicle';
  })();

  useEffect(() => {
    api
      .get('/interested-in/checkout/config')
      .then((res) => {
        if (res.data.success) setConfig(res.data.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingCards(true);
    api
      .get('/payment-cards')
      .then((res) => {
        if (res.data.success) setSavedCards(res.data.data || []);
      })
      .catch(() => setSavedCards([]))
      .finally(() => setLoadingCards(false));
  }, []);

  useEffect(() => {
    if (!returnFromSquare || !isPurchase) return;
    const walkInId = walkInIdFromUrl || pendingWalkIn?.id;
    if (!walkInId) return;

    const params = new URLSearchParams(window.location.search);
    const squareMock = params.get('squareMock') === '1';

    setConfirming(true);
    setError('');
    api
      .post('/interested-in/checkout/purchase/confirm', {
        walkInId,
        squareMock: squareMock || undefined,
      })
      .then((res) => {
        if (res.data.success) {
          onComplete(res.data.message, res.data.data);
        } else {
          setError(res.data.message || 'Payment confirmation failed');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Could not confirm payment');
      })
      .finally(() => setConfirming(false));
  }, [returnFromSquare, isPurchase, walkInIdFromUrl, pendingWalkIn?.id, onComplete]);

  const handlePurchasePay = async (cardPayload) => {
    const { data } = await api.post('/interested-in/checkout/purchase/pay', cardPayload);
    if (!data.success) {
      throw new Error(data.message || 'Payment failed');
    }
    onComplete(data.message, data.data);
  };

  const handleSellCard = async (cardPayload) => {
    const { data } = await api.post('/interested-in/checkout/sell/card-on-file', cardPayload);
    if (!data.success) {
      throw new Error(data.message || 'Failed to complete request');
    }
    onComplete(data.message, data.data);
  };

  if (confirming) {
    return (
      <section className="interested-step2">
        <p className="interested-step2-loading">
          <Loader2 size={22} className="interested-step2-spin" /> Confirming your payment…
        </p>
      </section>
    );
  }

  const purchasePayLabel = `Pay ${displayFee} securely with Square`;
  const purchasePayHint = 'Your card number never touches our website.';

  return (
    <section className="interested-step2">
      <div className="interested-step2-header">
        <span className="interested-step2-badge">Step 2 of 2</span>
        <h3>{isPurchase ? 'Pay sourcing fee' : 'Card on file'}</h3>
        <p className="interested-step2-lead">
          {isPurchase
            ? 'Pay securely with Square (recommended) or use a saved card below. Card details are never stored on our servers.'
            : 'Save a card on file for your visit — no charge today. We may use it for fees related to your trade or sale.'}
        </p>
      </div>

      {error && <div className="visit-us-alert visit-us-alert-error">{error}</div>}

      <div className="interested-step2-pricing">
        {vehicleSummary && (
          <div className="interested-step2-pricing-row">
            <div className="interested-step2-pricing-main">
              <span className="interested-step2-pricing-label">Your request</span>
              <p className="interested-step2-pricing-sublabel">{vehicleSummary}</p>
            </div>
          </div>
        )}
        <div className="interested-step2-pricing-row interested-step2-pricing-row--due">
          <div className="interested-step2-pricing-main">
            <span className="interested-step2-pricing-label">Due today</span>
            <p className="interested-step2-pricing-sublabel">
              {isPurchase ? 'Non-refundable sourcing fee' : 'No charge — card on file only'}
            </p>
          </div>
          <span className="interested-step2-pricing-amount">{isPurchase ? displayFee : '$0'}</span>
        </div>
        {isPurchase && (
          <div className="interested-step2-order-agreement">
            <p>
              By placing this order, I agree to the{' '}
              <Link to="/vehicle-sourcing-fee-agreement" target="_blank" rel="noopener noreferrer">
                Vehicle Sourcing Fee Agreement
              </Link>
              ,{' '}
              <Link to="/terms-of-service">Terms of Use</Link>, and{' '}
              <Link to="/privacy-policy">Privacy Notice</Link>.
            </p>
            <Link
              to="/vehicle-sourcing-fee-agreement"
              target="_blank"
              rel="noopener noreferrer"
              className="interested-step2-view-more"
            >
              View additional information
            </Link>
          </div>
        )}
      </div>

      <InterestedInCardForm
        submitLabel={isPurchase ? purchasePayLabel : 'Complete — card on file for visit'}
        payHint={isPurchase ? purchasePayHint : ''}
        submittingLabel={isPurchase ? 'Processing payment…' : 'Saving…'}
        onSubmit={isPurchase ? handlePurchasePay : handleSellCard}
        onError={setError}
        user={user}
        savedCards={savedCards}
        loadingCards={loadingCards}
        allowSavedCardPay={!isPurchase}
        storeCardLabel="Save this card to Payment Methods"
        storeCardHint="Shows under Profile → Payment Methods — Manage your credit and debit cards."
      />
    </section>
  );
}
